// eslint-disable-next-line
import i18n from "../src/locales/i18n";
import React from "react";
import ReactDOM from "react-dom";
import "./index.css";
import App from "./App";
import "bootstrap/dist/css/bootstrap.min.css";
import { Provider } from "react-redux";
import { store } from "./redux/store";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";

const PENDING_NAV_DB = "akanthas-push-nav";
const PENDING_NAV_STORE = "pending";
const PENDING_NAV_KEY = "notificationClick";

const navigateFromNotificationUrl = (url) => {
  try {
    const target = new URL(url, window.location.origin);
    if (target.origin !== window.location.origin) return;
    const next = target.pathname + target.search + target.hash;
    const current =
      window.location.pathname + window.location.search + window.location.hash;
    if (current === next) return;
    window.location.assign(next);
  } catch (error) {
    console.warn("Failed to navigate from notification click:", error);
  }
};

const consumePendingNotificationNavigation = async () => {
  if (typeof indexedDB === "undefined") return;

  try {
    const db = await new Promise((resolve, reject) => {
      const request = indexedDB.open(PENDING_NAV_DB, 1);
      request.onupgradeneeded = () => {
        const nextDb = request.result;
        if (!nextDb.objectStoreNames.contains(PENDING_NAV_STORE)) {
          nextDb.createObjectStore(PENDING_NAV_STORE);
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    const pending = await new Promise((resolve, reject) => {
      const tx = db.transaction(PENDING_NAV_STORE, "readwrite");
      const store = tx.objectStore(PENDING_NAV_STORE);
      const getReq = store.get(PENDING_NAV_KEY);
      getReq.onsuccess = () => {
        store.delete(PENDING_NAV_KEY);
        resolve(getReq.result || null);
      };
      getReq.onerror = () => reject(getReq.error);
    });
    db.close();

    if (!pending?.url) return;
    // Ignore stale entries older than 2 minutes.
    if (pending.at && Date.now() - pending.at > 2 * 60 * 1000) return;
    navigateFromNotificationUrl(pending.url);
  } catch (error) {
    console.warn("Failed to consume pending notification navigation:", error);
  }
};

const rootElement = document.getElementById("root");
ReactDOM.render(
  <Provider store={store}>
    <React.StrictMode>
      <App />
    </React.StrictMode>
  </Provider>,
  rootElement,
);

void consumePendingNotificationNavigation();

// SW posts this when a notification is tapped (existing PWA/tab).
if (typeof navigator !== "undefined" && navigator.serviceWorker) {
  navigator.serviceWorker.addEventListener("message", (event) => {
    if (event?.data?.type !== "NOTIFICATION_CLICK" || !event?.data?.url) {
      return;
    }
    navigateFromNotificationUrl(event.data.url);
  });
}

// Cold start / start_url races: retry pending nav after resume.
window.addEventListener("pageshow", () => {
  void consumePendingNotificationNavigation();
});
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") {
    void consumePendingNotificationNavigation();
  }
});

if (process.env.NODE_ENV !== "development") {
  serviceWorkerRegistration.register({
    // Notify the app that an update is available; UI decides when to refresh.
    onUpdate: (registration) => {
      try {
        window.dispatchEvent(
          new CustomEvent("sw-update", { detail: registration }),
        );
        console.info("Service Worker update detected.");
      } catch (e) {
        console.warn("Failed to dispatch sw-update event:", e);
      }
    },
  });
  console.info("Service Worker registration complete.");

  // Regularly check for service worker updates (e.g., every hour)
  setInterval(
    () => {
      if (navigator.serviceWorker?.getRegistration) {
        navigator.serviceWorker.getRegistration().then((reg) => {
          if (reg) {
            console.info("Checking for service worker update...");
            reg.update();
          }
        });
      }
    },
    30 * 60 * 1000,
  ); // every 1/2 hour
} else {
  serviceWorkerRegistration.unregister();
  console.info(
    "Service Worker registration is disabled and previous workers have been unregistered.",
  );
}
